import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  FormControl,
  InputLabel,
  TextField,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BNFInvestigation from "./bnfInvestigation";
import UploadDocument from "../../../components/uploadDocument/uploadDocument";
import { EBNFStatus, EUploadKey } from "../../../constants";
import { getBillNoFreightEditViewAPI } from "../../../API/getBillNoFreightEditViewAPI";
import { IBillNoFreight } from "../../../models/bnfException";
import { DateHelper, isPartiallyCleared } from "../../../helpers";
import {
  IFilterDropdown,
} from "../../../AppState/overagesFilterFormActions";
import AppState from "../../../AppState";
import { useDispatch, useSelector } from "react-redux";
import PageHeaderWithBack from "../../../components/PageHeaderWithBack";
import { useCommodityMaster } from "../../../@hooks/useCommodityMaster";
import ClearedInstructionDetails from "../../../components/cleared/clearedInstructionDetails";
import NOTIFICATION_TYPE from "../../../AppState/NotificationType";
import { PopoutNotification } from "../../../AppState/popoutNotification";
import { useAccountSpecialInstructions } from "../../../@hooks/useAccountSpecialInstructions";
import SpecialInstructionAccountField from "../../../components/specialInstructions/SpecialInstructionAccountField";
import SpecialInstructionsDialog from "../../../components/specialInstructions/SpecialInstructionsDialog";

const CustomTextField = ({
  id,
  label,
  md,
  value,
}: {
  id: string; // Change to string
  label: string;
  md: number;
  value: string | number | undefined; // Keep as is
}) => (
  <Grid item md={md} xs={12}>
    {id && (
      <FormControl fullWidth size="small">
        <InputLabel style={{ fontSize: "13px" }} shrink={true} id={id}>
          {label} <sup>&nbsp;</sup>
        </InputLabel>
        <TextField
          autoComplete="off"
          value={value ?? ""}
          id={id}
          data-testid={id}
          variant="outlined"
          disabled
        />
      </FormControl>
    )}
  </Grid>
);

const fields: {
  key: keyof IBillNoFreight | "";
  label: string;
  md: number;
}[] = [
  { key: "type", label: "Type", md: 3 },
  { key: "reporting", label: "Reported Terminal", md: 3 },
  { key: "proNumber", label: "PRO#", md: 3 },
  { key: "", label: "", md: 3 },
  { key: "origin", label: "Origin Terminal", md: 3 },
  { key: "destination", label: "Destination Terminal", md: 3 },
  { key: "trailer", label: "Last Trailer", md: 3 },
  { key: "lastManifest", label: "Last Manifest#", md: 3 },
  { key: "shipperAccount", label: "Shipper Account #", md: 3 },
  { key: "shipper", label: "Shipper Name", md: 3 },
  { key: "shipperAddress", label: "Shipper Address", md: 6 },
  { key: "shipperZip", label: "Shipper Zip", md: 3 },
  { key: "shipperCity", label: "Shipper City", md: 3 },
  { key: "shipperState", label: "Shipper State", md: 3 },
  { key: "", label: "", md: 3 },
  { key: "consigneeAccount", label: "Consignee Account #", md: 3 },
  { key: "consignee", label: "Consignee Name", md: 3 },
  { key: "consigneeAddress", label: "Consignee Address", md: 6 },
  { key: "consigneeZip", label: "Consignee Zip", md: 3 },
  { key: "consigneeCity", label: "Consignee City", md: 3 },
  { key: "consigneeState", label: "Consignee State", md: 3 },
  { key: "", label: "", md: 3 },
  { key: "commodity", label: "Commodity", md: 3 },
  { key: "package", label: "Package Type", md: 3 },
  { key: "pieces", label: "Total Pieces", md: 3 },
  { key: "", label: "", md: 3 },
  { key: "description", label: "Additional Details 1 ", md: 12 },
  { key: "supplementalDescription", label: "Additional Details 2", md: 12 },
];

const BnfEntry = () => {
  const { osdNumber } = useParams();
   const dispatch = useDispatch();
  const { loadCommodityOptions } = useCommodityMaster();
  const [bnfDetails, setBNFDetails] = React.useState<IBillNoFreight>();
  const [expanded, setExpanded] = React.useState<string[]>(["panel1"]);
  const [updatedDateTime, setUpdatedDateTime] = React.useState<string>("");
  const [bnfStatus, setBNFStatus] = React.useState<number>(
    EBNFStatus.exceptionEntry,
  );
  const isCleared = bnfDetails?.clearedInstructionDetails?.cleared;
  const isPartiallyClearedStatus = isPartiallyCleared(bnfDetails?.clearedInstructionDetails) && 
    (bnfStatus === EBNFStatus.needsCleared);
  const specialInstructions = useAccountSpecialInstructions({
    shipperAccount: bnfDetails?.shipperAccount,
    consigneeAccount: bnfDetails?.consigneeAccount,
  });

  const updateExpandedPanels = (
    prevExpanded: string[],
    panel: string,
    isExpanded: boolean,
  ): string[] => {
    if (isExpanded) {
      return prevExpanded.includes(panel)
        ? prevExpanded
        : [...prevExpanded, panel];
    }

    return prevExpanded.filter((item) => item !== panel);
  };

  const handleChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded((prevExpanded) =>
        updateExpandedPanels(prevExpanded, panel, isExpanded),
      );
    };

  const commodityCodeOptions = useSelector(
    (state: AppState) => state.commodityMasterData,
  );

  const panels = [
    {
      id: "panel1",
      title: "Bill No Freight Exception",
      content: (
        <Grid container spacing={2} className="filter-margin">
          {fields.map(({ key, label, md }) => (
            key === "shipperAccount" ? (
              <SpecialInstructionAccountField
                key={key}
                id="shipperAccount"
                label={label}
                md={md}
                value={bnfDetails?.shipperAccount}
                showIcon={specialInstructions.hasShipperSpecialInstructions}
                onIconClick={() => specialInstructions.openSpecialInstructions("shipper")}
                iconTestId="shipper-special-instructions-icon"
              />
            ) : key === "consigneeAccount" ? (
              <SpecialInstructionAccountField
                key={key}
                id="consigneeAccount"
                label={label}
                md={md}
                value={bnfDetails?.consigneeAccount}
                showIcon={specialInstructions.hasConsigneeSpecialInstructions}
                onIconClick={() => specialInstructions.openSpecialInstructions("consignee")}
                iconTestId="consignee-special-instructions-icon"
              />
            ) : (
              <CustomTextField
                key={key}
                id={key?.toString() || ""}
                value={
                  key && bnfDetails
                    ? (bnfDetails as Record<string, string | number | undefined>)[key]
                    : undefined
                }
                label={label}
                md={md}
              />
            )
          ))}
        </Grid>
      ),
    },
    {
      id: "panel2",
      title: "Bill No Freight Follow Up",
      content: (
        <BNFInvestigation
          key={`${expanded} bnf-Investigation`}
          osdNumber={Number.parseInt(osdNumber!)}
          hasMovementSinceBNFValue={bnfDetails?.hasMovementSinceBNF}
          investigationStatusNumber={bnfDetails?.investigationStatusNumber}
          investigationF18Note={bnfDetails?.f18Note}
          setBNFStatus={setBNFStatus}
          isCleared={isCleared}
        />
      ),
    },
    {
      id: "panel3",
      title: isCleared && !isPartiallyClearedStatus ? "Cleared Instruction Details": "Upload Documents",
      content: (
        isCleared && !isPartiallyClearedStatus ?
          <ClearedInstructionDetails
            clearedInstructionDetails={bnfDetails?.clearedInstructionDetails}
          />
        : <UploadDocument
          key={expanded.toString() || "upload-document"}
          osdNumber={Number.parseInt(osdNumber!)}
          uploadKey={EUploadKey.pro}
        />
      ),
    },
  ];

  // Add panel4 for partially cleared instruction details
  if (isPartiallyClearedStatus) {
    panels.push({
      id: "panel4",
      title: "Cleared Instruction Details",
      content: (
        <ClearedInstructionDetails
          clearedInstructionDetails={bnfDetails?.clearedInstructionDetails}
        />
      ),
    });
  }

  useEffect(() => {
    if (osdNumber) {
      const viewBNFDetails = async () => {
        let bnfResponse;
        
        try {
          bnfResponse = await getBillNoFreightEditViewAPI(osdNumber);
        } catch (error) {
          console.error("Failed to fetch Bill No Freight details:", error);
          dispatch(
            PopoutNotification(
              NOTIFICATION_TYPE.ERROR,
              typeof error === "string" ? error : `OS&D # ${osdNumber} not found`,
              Date.now().toString(),
            ),
          );
          bnfResponse = undefined;
        }
        const lastUpdatedDate = bnfResponse?.lastUpdatedDate;
        const lastUpdatedTime = bnfResponse?.lastUpdatedTime;
        setBNFStatus(
          bnfResponse?.investigationStatusNumber ?? EBNFStatus.exceptionEntry,
        );
        if (lastUpdatedDate && lastUpdatedTime) {
          setUpdatedDateTime(
            DateHelper.mergerAS400DateTime(lastUpdatedDate, lastUpdatedTime),
          );
        }
        setBNFDetails(bnfResponse);
      };
      viewBNFDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [osdNumber]);

  useEffect(() => {
    if (!commodityCodeOptions?.length) {
     loadCommodityOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadCommodityOptions, commodityCodeOptions?.length]);

  useEffect(() => {
    if (commodityCodeOptions?.length && bnfDetails?.commodity) {
      const commodity = commodityCodeOptions?.find(
        (e: IFilterDropdown) => e.code === bnfDetails.commodity,
      );

      if (commodity) {
        setBNFDetails((prevState) => ({
          ...prevState,
          commodity: `${commodity.code} - ${commodity.displayValue}`,
        }));
      }
    }
  }, [commodityCodeOptions, bnfDetails?.commodity]);

  return (
    <div className="bg-layout overage-entry">
      {/* Header */}
      <PageHeaderWithBack
        osdNumber={bnfDetails?.osdNumber}
        proNumber={bnfDetails?.proNumber}
        updatedDateTime={updatedDateTime}
        headingTitle={"Bill No Freight"}
      />
      {/* Body */}
      <div className="content-area">
        {panels.map(({ id, title, content }) => (
          <Accordion
            className="accordion"
            expanded={expanded.includes(id)}
            onChange={handleChange(id)}
            key={id}
          >
            <AccordionSummary
              className="header"
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${id}-content`}
              id={`${id}-header`}
              key={`${id}-header`}
              data-testid={`${id}-header`}
            >
              <Typography
                className={
                  isCleared || id === "panel1" ||
                  ((id === "panel2" && bnfStatus === EBNFStatus.needsCleared))
                    ? "queue-check queue-check-gc"
                    : "queue-check"
                }
              >
                {id.slice(-1)}
              </Typography>
              <Typography
                className="accordion-header"
                data-testid={`${id}-title`}
              >
                {title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>{content}</AccordionDetails>
          </Accordion>
        ))}
      </div>
      <SpecialInstructionsDialog
        open={specialInstructions.isDialogOpen}
        title={specialInstructions.openedSpecialInstructionsTitle}
        content={specialInstructions.openedSpecialInstructionsText}
        onClose={specialInstructions.closeSpecialInstructions}
      />
    </div>
  );
};

export default BnfEntry;
